import { Fragment, type ReactNode } from 'react';

import { renderIcons } from './icon-tokens';

/**
 * The bit of markdown bot replies use — links, bold, italics, bullet and
 * numbered lists, paragraphs — rendered as React elements, never as HTML, so
 * a reply can't inject markup. Font Awesome tokens (`<<fas fa-home>>`) draw
 * as icons (`icon-tokens.tsx`). Anything else stays plain text.
 */

const INLINE =
  /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)|\*\*([^*\n]+)\*\*|__([^_\n]+)__|\*([^*\n]+)\*|(https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"])/g;

const link = (href: string, label: ReactNode, key: number) => (
  <a key={key} href={href} target="_blank" rel="noopener noreferrer">
    {label}
  </a>
);

export const renderInline = (text: string): ReactNode[] => {
  const out: ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const m of text.matchAll(INLINE)) {
    const at = m.index ?? 0;
    if (at > last) out.push(...renderIcons(text.slice(last, at), `t${at}`));
    const [, label, href, bold, bold2, italic, bare] = m;
    if (href) out.push(link(href, label, key++));
    else if (bold || bold2)
      out.push(<strong key={key++}>{bold || bold2}</strong>);
    else if (italic) out.push(<em key={key++}>{italic}</em>);
    else if (bare) out.push(link(bare, bare, key++));
    last = at + m[0].length;
  }
  if (last < text.length) out.push(...renderIcons(text.slice(last), 'end'));
  return out;
};

const BULLET = /^\s*[-*•]\s+/;
const NUMBERED = /^\s*\d+[.)]\s+/;

type Block =
  | { kind: 'p'; lines: string[] }
  | { kind: 'ul' | 'ol'; items: string[] };

const toBlocks = (text: string): Block[] => {
  const blocks: Block[] = [];
  text.split('\n').forEach((line) => {
    const prev = blocks[blocks.length - 1];
    const kind = BULLET.test(line) ? 'ul' : NUMBERED.test(line) ? 'ol' : null;
    if (kind) {
      const item = line.replace(kind === 'ul' ? BULLET : NUMBERED, '');
      if (prev?.kind === kind) prev.items.push(item);
      else blocks.push({ kind, items: [item] });
    } else if (!line.trim()) {
      blocks.push({ kind: 'p', lines: [] });
    } else if (prev?.kind === 'p') {
      prev.lines.push(line);
    } else {
      blocks.push({ kind: 'p', lines: [line] });
    }
  });
  return blocks.filter((b) => b.kind !== 'p' || b.lines.length);
};

export const BubbleMarkdown = ({ text }: { text: string }) => (
  <>
    {toBlocks(text).map((b, i) =>
      b.kind === 'p' ? (
        <p key={i}>
          {b.lines.map((line, j) => (
            <Fragment key={j}>
              {j > 0 && <br />}
              {renderInline(line)}
            </Fragment>
          ))}
        </p>
      ) : b.kind === 'ul' ? (
        <ul key={i}>
          {b.items.map((item, j) => (
            <li key={j}>{renderInline(item)}</li>
          ))}
        </ul>
      ) : (
        <ol key={i}>
          {b.items.map((item, j) => (
            <li key={j}>{renderInline(item)}</li>
          ))}
        </ol>
      )
    )}
  </>
);
